/**
 * Preset initial generator sets for the Immunity Distillation demo (RD-65).
 *
 * Each generator is a REAL gl(n,ℂ) element X — a `ComplexMatrix` tangent vector
 * built with `cmatFromReal` (or with explicit imaginary parts to exercise ℂ).
 * Each preset is an arbitrary ALLOW₀ ⊆ gl(n,ℂ) the operator might hand VID,
 * together with the authority subgroup H ⊆ GL(n,ℂ) the operator supplies — the
 * SOLE A1 governance choice. A generator violates A1 exactly when its group
 * image exp(X) lands outside H; that verdict is COMPUTED live on the @csk/core
 * Mₙ(ℂ) realization, never asserted. A2/A3/A4 hold for every gl(n,ℂ) generator
 * by construction.
 *
 * The presets span gl(2,ℂ) and gl(3,ℂ) — VID's original generator set was an
 * upper-triangular unipotent GL(3) family, restored here as the
 * `unipotent-flow` preset — and vary in how many generators violate A1, so
 * distillation takes different step counts: one is already immune (zero steps),
 * the others distill down through one or two A1 violators. This module is pure
 * data.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type {
  Complex,
  ComplexMatrix,
  GeneratorSet,
} from "../algorithms/types.js";
import { cmatFromReal, complexMatrix } from "@csk/core/linear";

/** Build a 2×2 ComplexMatrix with explicit complex entries [[z00, z01], [z10, z11]]. */
const mat = (
  z00: Complex,
  z01: Complex,
  z10: Complex,
  z11: Complex,
): ComplexMatrix =>
  complexMatrix([
    [z00, z01],
    [z10, z11],
  ]);

export const GENERATOR_SETS: readonly [GeneratorSet, ...GeneratorSet[]] = [
  {
    id: "raw-specification",
    title: "Raw specification (gl(2,ℂ))",
    summary:
      "An arbitrary generator set lifted straight from a system spec, audited against the special-linear authority subgroup SL(2,ℂ) = {g : det g = 1}. exp(X) ∈ SL(2,ℂ) ⇔ tr(X) = 0, so the two nonzero-trace generators are A1 violators. Distillation strips them in two steps, leaving three immune generators.",
    authoritySubgroup: "special-linear",
    generators: [
      {
        id: "E12",
        label: "E₁₂ shear",
        description: "strictly-upper X = [[0,1],[0,0]] · tr 0 · exp ∈ SL(2,ℂ)",
        matrix: cmatFromReal([
          [0, 1],
          [0, 0],
        ]),
      },
      {
        id: "Hi",
        label: "iσ₃ phase",
        description:
          "X = [[i,1],[0,−i]] · tr 0 · complex diagonal · exp ∈ SL(2,ℂ)",
        matrix: mat([0, 1], [1, 0], [0, 0], [0, -1]),
      },
      {
        id: "R",
        label: "rotation block",
        description: "skew X = [[0,1],[−1,0]] · tr 0 · exp ∈ SL(2,ℂ)",
        matrix: cmatFromReal([
          [0, 1],
          [-1, 0],
        ]),
      },
      {
        id: "S",
        label: "scaling generator",
        description: "X = I · tr 2 ≠ 0 · det(exp X) = e² ≠ 1 — A1 violator",
        matrix: cmatFromReal([
          [1, 0],
          [0, 1],
        ]),
      },
      {
        id: "D",
        label: "dilation",
        description:
          "X = diag(0.6, 0.2) · tr 0.8 ≠ 0 · det(exp X) ≠ 1 — A1 violator",
        matrix: cmatFromReal([
          [0.6, 0],
          [0, 0.2],
        ]),
      },
    ],
  },
  {
    id: "audit-snapshot",
    title: "Runtime audit snapshot (gl(2,ℂ))",
    summary:
      "A GPU-runtime generator set audited against the upper-triangular authority subgroup (Borel) — exp(X) ∈ H ⇔ its lower-left entry vanishes. The rotation and the lower shear leak below the diagonal, so they are A1 violators. Distillation converges in two steps to two immune generators.",
    authoritySubgroup: "upper-triangular",
    generators: [
      {
        id: "U",
        label: "upper shear",
        description: "X = [[0,1.5],[0,0]] · exp upper-triangular ∈ H",
        matrix: cmatFromReal([
          [0, 1.5],
          [0, 0],
        ]),
      },
      {
        id: "Ud",
        label: "upper drift",
        description: "X = [[0.5,2],[0,−0.5]] · exp upper-triangular ∈ H",
        matrix: cmatFromReal([
          [0.5, 2],
          [0, -0.5],
        ]),
      },
      {
        id: "R",
        label: "rotation generator",
        description:
          "skew X = [[0,0.7],[−0.7,0]] · exp leaks below the diagonal — A1 violator",
        matrix: cmatFromReal([
          [0, 0.7],
          [-0.7, 0],
        ]),
      },
      {
        id: "L",
        label: "lower shear",
        description:
          "X = [[0,0],[0.9,0]] · exp lower-triangular · escapes the Borel — A1 violator",
        matrix: cmatFromReal([
          [0, 0],
          [0.9, 0],
        ]),
      },
    ],
  },
  {
    id: "weight-matrix",
    title: "Neural weight components (gl(2,ℂ))",
    summary:
      "Weight-matrix generators after a gradient step (RD-61), audited against the diagonal torus — exp(X) ∈ H ⇔ both off-diagonal entries vanish. A single off-diagonal coupling drifts off the torus; distillation converges in one step to three immune generators.",
    authoritySubgroup: "diagonal",
    generators: [
      {
        id: "W1",
        label: "diagonal mode α",
        description: "X = diag(0.4, −0.2) · exp stays on the torus ∈ H",
        matrix: cmatFromReal([
          [0.4, 0],
          [0, -0.2],
        ]),
      },
      {
        id: "W2",
        label: "diagonal mode β",
        description: "X = diag(−0.3, 0.5) · exp stays on the torus ∈ H",
        matrix: cmatFromReal([
          [-0.3, 0],
          [0, 0.5],
        ]),
      },
      {
        id: "Wd",
        label: "off-diagonal coupling",
        description:
          "X = [[0,0.6],[0.6,0]] · exp has nonzero off-diagonals — A1 violator",
        matrix: cmatFromReal([
          [0, 0.6],
          [0.6, 0],
        ]),
      },
      {
        id: "W3",
        label: "complex diagonal mode γ",
        description:
          "X = diag(0.1+0.2i, 0) · complex diagonal · exp stays on the torus ∈ H",
        matrix: mat([0.1, 0.2], [0, 0], [0, 0], [0, 0]),
      },
    ],
  },
  {
    id: "already-immune",
    title: "Pre-hardened torus (gl(2,ℂ))",
    summary:
      "A generator set already inside the diagonal torus. Every exp(X) is diagonal, so every generator is A1-compliant and the set is its own fixed point — zero distillation steps.",
    authoritySubgroup: "diagonal",
    generators: [
      {
        id: "T1",
        label: "diagonal mode α",
        description: "X = diag(0.3, −0.3) · tr 0 · exp diagonal ∈ H",
        matrix: cmatFromReal([
          [0.3, 0],
          [0, -0.3],
        ]),
      },
      {
        id: "T2",
        label: "complex diagonal mode β",
        description: "X = diag(0.4i, 0) · complex diagonal · exp diagonal ∈ H",
        matrix: mat([0, 0.4], [0, 0], [0, 0], [0, 0]),
      },
      {
        id: "T3",
        label: "diagonal mode γ",
        description: "X = diag(−0.5, 0.5) · tr 0 · exp diagonal ∈ H",
        matrix: cmatFromReal([
          [-0.5, 0],
          [0, 0.5],
        ]),
      },
    ],
  },
  {
    id: "unipotent-flow",
    title: "Unipotent control flow (gl(3,ℂ))",
    summary:
      "VID's original generator set: an upper-triangular unipotent GL(3) family of strictly-upper control-flow generators, audited against the upper-triangular authority subgroup (Borel) ⊆ GL(3,ℂ) — exp(X) ∈ H ⇔ every below-diagonal entry vanishes. The three strictly-upper generators exponentiate to unipotent upper-triangular elements and stay inside H; the two generators with a below-diagonal entry leak under the diagonal, so they are A1 violators. Distillation converges in two steps to three immune generators.",
    authoritySubgroup: "upper-triangular",
    generators: [
      {
        id: "N12",
        label: "12-shear",
        description:
          "X = [[0,1,0],[0,0,0],[0,0,0]] · strictly upper · exp unipotent ∈ H",
        matrix: cmatFromReal([
          [0, 1, 0],
          [0, 0, 0],
          [0, 0, 0],
        ]),
      },
      {
        id: "N13",
        label: "13-shear",
        description:
          "X = [[0,0,2],[0,0,0],[0,0,0]] · strictly upper · exp unipotent ∈ H",
        matrix: cmatFromReal([
          [0, 0, 2],
          [0, 0, 0],
          [0, 0, 0],
        ]),
      },
      {
        id: "Nfull",
        label: "full unipotent flow",
        description:
          "X = [[0,1,3],[0,0,4],[0,0,0]] · strictly upper · exp = unipotent [[1,a,b],[0,1,c],[0,0,1]] ∈ H",
        matrix: cmatFromReal([
          [0, 1, 3],
          [0, 0, 4],
          [0, 0, 0],
        ]),
      },
      {
        id: "L21",
        label: "21-leak",
        description:
          "X = [[0,0,0],[0.5,0,0],[0,0,0]] · exp has a nonzero (2,1) entry below the diagonal — A1 violator",
        matrix: cmatFromReal([
          [0, 0, 0],
          [0.5, 0, 0],
          [0, 0, 0],
        ]),
      },
      {
        id: "L31",
        label: "31-leak",
        description:
          "X = [[0,0,0],[0,0,0],[0.7,0,0]] · exp leaks into the (3,1) corner below the diagonal — A1 violator",
        matrix: cmatFromReal([
          [0, 0, 0],
          [0, 0, 0],
          [0.7, 0, 0],
        ]),
      },
    ],
  },
];
